import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Typography,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import ArticleIcon from "@mui/icons-material/Article";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadIcon from "@mui/icons-material/Download";
import VideoMessage from "../MessageHistory/VideoMessage";
import AudioMessage from "../MessageHistory/AudioMessage";
import {
  SIMULATOR_ATTACHMENT_LABELS,
  resolveSimulatorAttachmentKind,
} from "../../utils/agentSimulatorAttachmentKind";

const KIND_META = {
  pdf: { Icon: PictureAsPdfIcon, color: "#E5252A", bg: "#FDECEC" },
  document: { Icon: DescriptionIcon, color: "#2B579A", bg: "#E8EEF7" },
  spreadsheet: { Icon: TableChartIcon, color: "#217346", bg: "#E7F4EC" },
  archive: { Icon: FolderZipIcon, color: "#6B4FBB", bg: "#F0EBFA" },
  text: { Icon: ArticleIcon, color: "#5C6BC0", bg: "#ECEEF9" },
  file: { Icon: InsertDriveFileIcon, color: "#546E7A", bg: "#ECEFF1" },
};

function FileCard({ url, fileName, kind, isOwn = false }) {
  const meta = KIND_META[kind] || KIND_META.file;
  const Icon = meta.Icon;
  const label = SIMULATOR_ATTACHMENT_LABELS[kind] || SIMULATOR_ATTACHMENT_LABELS.file;

  return (
    <Box
      component={Link}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      underline="none"
      download={fileName || undefined}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.25,
        py: 1,
        borderRadius: 1.5,
        border: `1px solid ${alpha(theme.palette.divider, isOwn ? 0.35 : 1)}`,
        bgcolor: isOwn ? alpha(theme.palette.common.white, 0.14) : alpha(theme.palette.common.white, 0.92),
        color: "inherit",
        minWidth: 0,
        maxWidth: "100%",
        transition: "background-color 0.15s, box-shadow 0.15s",
        "&:hover": {
          bgcolor: isOwn ? alpha(theme.palette.common.white, 0.22) : theme.palette.background.paper,
          boxShadow: 1,
        },
      })}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: meta.bg,
          color: meta.color,
          flexShrink: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={600} noWrap title={fileName}>
          {fileName || label}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label} · Clique para abrir
        </Typography>
      </Box>
      <DownloadIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
    </Box>
  );
}

export default function AgentSimulatorAttachment({ attachment, isOwn = false }) {
  const [imageError, setImageError] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  if (!attachment?.url) return null;

  const kind =
    attachment.kind ||
    resolveSimulatorAttachmentKind({
      messageType: attachment.messageType,
      contentType: attachment.contentType,
      url: attachment.url,
      fileName: attachment.fileName,
    });

  if (kind === "image") {
    return (
      <>
        {imageError ? (
          <ImageNotSupportedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
        ) : (
          <Box
            component="img"
            src={attachment.url}
            alt={attachment.fileName || "Imagem"}
            onClick={() => setPreviewOpen(true)}
            onError={() => setImageError(true)}
            sx={{
              display: "block",
              width: "100%",
              maxWidth: 240,
              maxHeight: 220,
              borderRadius: 1.5,
              objectFit: "cover",
              cursor: "pointer",
              border: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}`,
            }}
          />
        )}
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg">
          <DialogContent
            sx={{
              p: 0,
              bgcolor: "black",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <IconButton
              onClick={() => setPreviewOpen(false)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "common.white",
                bgcolor: alpha("#000", 0.45),
                "&:hover": { bgcolor: alpha("#000", 0.65) },
              }}
            >
              <CloseIcon />
            </IconButton>
            {!imageError && (
              <Box
                component="img"
                src={attachment.url}
                alt={attachment.fileName || "Imagem"}
                sx={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain" }}
                onError={() => setImageError(true)}
              />
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (kind === "video") {
    return (
      <Box sx={{ maxWidth: 260, borderRadius: 1.5, overflow: "hidden" }}>
        <VideoMessage message={{ video: attachment.url, url: attachment.url }} />
      </Box>
    );
  }

  if (kind === "audio") {
    return (
      <Box sx={{ minWidth: 220, maxWidth: "100%" }}>
        <AudioMessage message={{ audio: attachment.url, url: attachment.url }} />
      </Box>
    );
  }

  return (
    <FileCard
      url={attachment.url}
      fileName={attachment.fileName}
      kind={kind}
      isOwn={isOwn}
    />
  );
}
